/**
 * Progress screen. Answers two questions for the student: "what am I doing
 * well?" and "what should I practice next?" - without overwhelming analytics.
 *
 * Diagnostics are worded strictly as correlations ("N of your last M ...") and
 * never as verdicts about understanding, matching the responsible-diagnostics
 * commitment.
 */

import type { Screen } from "../router";
import type { Store } from "../state";
import { el } from "../../ui/dom";
import { UNITS, getTopic, topicsInUnit } from "../../curriculum/curriculum";
import { subskillLabel } from "../../curriculum/subskills";
import {
  MASTERY_LABEL,
  type SessionRecord,
  type SubskillTrend,
} from "../../progress/model";
import {
  getProgressRepository,
  type ProgressRepository,
} from "../../progress/repository";
import { hasGenerator } from "../../topics/registry";
import { recommendedTopics } from "../question-select";

export const progressScreen: Screen = (store, mount) => {
  const repo = getProgressRepository();
  mount.appendChild(el("h1", {}, "Progress"));

  if (!repo) {
    mount.appendChild(
      el("p", { class: "muted" }, "Progress is unavailable in this environment."),
    );
    return;
  }

  const taught = store.taughtSet();

  // This Week.
  const w = repo.weekly();
  const firstTryPct =
    w.firstTryAccuracy === null ? "-" : `${Math.round(w.firstTryAccuracy * 100)}%`;
  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "This Week"),
      el(
        "div",
        { class: "stat-grid" },
        stat("Questions Practiced", String(w.questionsPracticed)),
        stat("First-Try Accuracy", firstTryPct),
        stat("Quizzes Completed", String(w.quizzesCompleted)),
      ),
    ),
  );

  // Per-topic status, by unit. Only show topics with a generator (the rest are
  // "coming soon" and have no meaningful status yet).
  for (const unit of UNITS) {
    const rows: HTMLElement[] = [];
    for (const topic of topicsInUnit(unit.id)) {
      if (!hasGenerator(topic.id)) continue;
      const state = repo.mastery(topic.id);
      const label = MASTERY_LABEL[state];
      const taughtHint = taught.has(topic.id) ? "" : " (not yet taught)";
      rows.push(
        el(
          "li",
          { class: "topic-item" },
          el("span", { class: "topic-name" }, topic.name + taughtHint),
          el(
            "span",
            { class: state === "strong" ? "badge badge-strong" : "badge" },
            label,
          ),
        ),
      );
    }
    if (rows.length > 0) {
      mount.appendChild(
        el(
          "section",
          { class: "card" },
          el("p", { class: "card-title" }, `Unit ${unit.order}: ${unit.name}`),
          el("ul", { class: "topic-list" }, ...rows),
        ),
      );
    }
  }

  // Needs Practice - correlation-only wording.
  const concerns = repo.concerns();
  if (concerns.length > 0) {
    const items = concerns.map((c) =>
      el(
        "li",
        { class: "topic-item" },
        el("span", { class: "topic-name" }, subskillLabel(c.subskill)),
        el(
          "span",
          { class: "badge" },
          `${c.recentCorrect} of last ${c.recentTotal} correct`,
        ),
      ),
    );
    // Recommend a targeted review built from the weak subskills.
    const recTopics = recommendedTopics(
      concerns.map((c) => c.subskill),
      taught,
    );

    const needsCard = el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "Needs Practice"),
      el(
        "p",
        { class: "muted", style: "margin-top:0;font-size:0.85rem" },
        "Recent questions involving these skills were missed more often. This is a pattern to review, not a judgment.",
      ),
      el("ul", { class: "topic-list" }, ...items),
    );
    if (recTopics.length > 0) {
      needsCard.appendChild(
        el(
          "div",
          { class: "btn-row" },
          el(
            "button",
            {
              class: "btn btn-primary",
              onClick: () =>
                store.navigate("quiz", {
                  autostart: {
                    scope: { kind: "recommended", topics: recTopics },
                    difficulty: "standard",
                    count: 8,
                  },
                }),
            },
            "Practice these (8 questions)",
          ),
        ),
      );
    }
    mount.appendChild(needsCard);
  }

  // Recent activity trend.
  renderTrend(mount, repo.sessions());

  // Skill growth over time.
  renderSkillGrowth(mount, repo.trends());

  // Backup / restore.
  renderBackup(store, mount, repo);

  // Storage note.
  mount.appendChild(
    el(
      "p",
      { class: "muted", style: "font-size:0.8rem;margin-top:1rem" },
      "Progress is saved on this device only. Clearing your browser data or using private browsing will reset it - use Back up progress to keep a copy.",
    ),
  );
};

/** A compact recent-sessions view with a simple accuracy bar per session. */
function renderTrend(mount: HTMLElement, sessions: readonly SessionRecord[]): void {
  if (sessions.length === 0) return;
  // Most recent first, capped so the list stays readable.
  const recent = [...sessions].slice(-10).reverse();

  const rows = recent.map((s) => {
    const pct = s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0;
    const label =
      s.topic === "mixed" ? "Mixed" : getTopic(s.topic).name;
    const when = new Date(s.at).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    return el(
      "li",
      { class: "trend-row" },
      el(
        "div",
        { class: "trend-head" },
        el("span", { class: "trend-topic" }, `${label}`),
        el(
          "span",
          { class: "trend-meta" },
          `${s.kind === "quiz" ? "Quiz" : "Practice"} \u00b7 ${when} \u00b7 ${s.correct}/${s.total}`,
        ),
      ),
      el(
        "div",
        { class: "trend-track" },
        el("div", { class: "trend-fill", style: `width:${pct}%` }),
      ),
    );
  });

  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "Recent Activity"),
      el("ul", { class: "trend-list" }, ...rows),
    ),
  );
}

/** A small accuracy sparkline per subskill, with a plain direction note. */
function renderSkillGrowth(mount: HTMLElement, trends: readonly SubskillTrend[]): void {
  if (trends.length === 0) return;

  const DIRECTION_NOTE: Record<SubskillTrend["direction"], string> = {
    improving: "Recent sessions have been more accurate than earlier ones.",
    steady: "Accuracy has held about the same across recent sessions.",
    dipping: "Recent sessions have been less accurate than earlier ones.",
  };

  const rows = trends.slice(0, 8).map((tr) => {
    // A row of bars, one per snapshot, height = accuracy.
    const bars = tr.points.map((p) =>
      el("span", {
        class: "spark-bar",
        style: `height:${Math.max(6, Math.round(p.accuracy * 100))}%`,
        title: `${Math.round(p.accuracy * 100)}%`,
      }),
    );
    return el(
      "li",
      { class: "growth-row" },
      el(
        "div",
        { class: "growth-head" },
        el("span", { class: "growth-name" }, subskillLabel(tr.subskill)),
        el("span", { class: `growth-tag growth-${tr.direction}` }, tr.direction),
      ),
      el("div", { class: "spark" }, ...bars),
      el("p", { class: "muted", style: "margin:0.3rem 0 0;font-size:0.8rem" }, DIRECTION_NOTE[tr.direction]),
    );
  });

  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "Skill Growth"),
      el(
        "p",
        { class: "muted", style: "margin-top:0;font-size:0.85rem" },
        "Accuracy on each skill across your recent sessions. A pattern over time, not a judgment.",
      ),
      el("ul", { class: "growth-list" }, ...rows),
    ),
  );
}

/** Export/import controls so progress survives a cleared browser or a device switch. */
function renderBackup(
  store: Store,
  mount: HTMLElement,
  repo: ProgressRepository,
): void {
  const status = el("p", { class: "muted", style: "margin:0.5rem 0 0;font-size:0.85rem" });

  const exportBtn = el(
    "button",
    {
      class: "btn",
      onClick: () => {
        const json = repo.exportJson();
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "axle-progress.json";
        a.click();
        URL.revokeObjectURL(url);
      },
    },
    "Back up progress",
  );

  const fileInput = el("input", {
    type: "file",
    accept: "application/json,.json",
    style: "display:none",
  }) as HTMLInputElement;
  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    file
      .text()
      .then((text) => {
        const ok = repo.importJson(text);
        if (ok) {
          status.textContent = "Progress restored.";
          // Re-render the screen to reflect imported data.
          store.navigate("progress");
        } else {
          status.textContent = "That file was not a valid AXLE progress backup.";
        }
      })
      .catch(() => {
        status.textContent = "Could not read that file.";
      });
  });

  const importBtn = el(
    "button",
    { class: "btn", onClick: () => fileInput.click() },
    "Restore from backup",
  );

  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "Backup & Restore"),
      el(
        "p",
        { class: "muted", style: "margin:0 0 0.75rem;font-size:0.85rem" },
        "Save your progress to a file, or load it on another device.",
      ),
      el("div", { class: "btn-row", style: "margin-top:0" }, exportBtn, importBtn, fileInput),
      status,
    ),
  );
}

function stat(label: string, value: string): HTMLElement {
  return el(
    "div",
    { class: "stat" },
    el("div", { class: "stat-value" }, value),
    el("div", { class: "stat-label" }, label),
  );
}
